import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';

function ExamList() {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userPremiumStatus, setUserPremiumStatus] = useState(false);
  const [userTier, setUserTier] = useState('free');
  const [userExamType, setUserExamType] = useState('selective');

  useEffect(() => {
    fetchExams();
    checkPremiumStatus();
  }, []);

  const checkPremiumStatus = async () => {
    try {
      // Get current user from Cognito using Amplify v6 syntax
      const { fetchUserAttributes } = await import('aws-amplify/auth');
      const attributes = await fetchUserAttributes();
      
      // Check if user has premium access (stored in custom attribute)
      const premiumVal = attributes['custom:premium_status'] || 'false';
      const isPremium = premiumVal === 'true' || premiumVal === 'essential';
      setUserPremiumStatus(isPremium);
      if (premiumVal === 'essential') setUserTier('essential');
      else if (premiumVal === 'true' || premiumVal === 'premium') setUserTier('premium');
      else setUserTier('free');
      
      // Get exam type
      setUserExamType(attributes['custom:exam_type'] || 'selective');
    } catch (err) {
      console.error('Error checking premium status:', err);
      setUserPremiumStatus(false);
    }
  };

  const fetchExams = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/exams');
      setExams(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching exams:', err);
      setError('Failed to load exams. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Group exams by topic - Filter based on tier
  const getExamsByTopic = (topic) => {
    return exams
      .filter(exam => {
        const matchesTopic = exam.topic && exam.topic.toLowerCase().includes(topic.toLowerCase());
        
        // Free users: only MockTests
        if (userTier === 'free') {
          return matchesTopic && exam.category === 'MockTests';
        }
        
        // Essential users: MockTests + TopicBased test 1 only (no PaidBundle)
        if (userTier === 'essential') {
          if (exam.category === 'PaidBundle') return false;
          if (exam.category === 'TopicBased') {
            // Only include test 1 (examId without -2 or -3 suffix)
            const isTest2or3 = /-[23]$/.test(exam.examId);
            return matchesTopic && !isTest2or3;
          }
          return matchesTopic;
        }
        
        // Premium: everything
        return matchesTopic;
      })
      .sort((a, b) => a.title.localeCompare(b.title));
  };

  // Group exams by topic and category - Filter based on tier
  const getExamsByTopicAndCategory = (topic, category) => {
    return exams
      .filter(exam => {
        const matchesTopic = exam.topic && exam.topic.toLowerCase().includes(topic.toLowerCase());
        const matchesCategory = exam.category === category;
        
        if (!matchesTopic || !matchesCategory) return false;
        
        // Free users: only MockTests
        if (userTier === 'free') {
          return category === 'MockTests';
        }
        
        // Essential users: MockTests + TopicBased test 1 only (no PaidBundle)
        if (userTier === 'essential') {
          if (category === 'PaidBundle') return false;
          if (category === 'TopicBased') {
            const isTest2or3 = /-[23]$/.test(exam.examId);
            return !isTest2or3;
          }
          return true;
        }
        
        // Premium: everything
        return true;
      })
      .sort((a, b) => a.title.localeCompare(b.title));
  };

  const categories = [
    {
      id: 'reading',
      name: 'Reading',
      icon: '📖',
      description: 'Comprehension, vocabulary, and text analysis',
      color: '#10b981',
      exams: getExamsByTopic('reading'),
      mockTests: getExamsByTopicAndCategory('reading', 'MockTests'),
      topicTests: getExamsByTopicAndCategory('reading', 'TopicBased'),
      paidTests: getExamsByTopicAndCategory('reading', 'PaidBundle')
    },
    {
      id: 'maths',
      name: 'Mathematical Reasoning',
      icon: '🔢',
      description: 'Numerical reasoning, problem solving, and mathematical concepts',
      color: '#4f46e5',
      exams: getExamsByTopic('math'),
      mockTests: getExamsByTopicAndCategory('math', 'MockTests'),
      topicTests: getExamsByTopicAndCategory('math', 'TopicBased'),
      paidTests: getExamsByTopicAndCategory('math', 'PaidBundle')
    },
    {
      id: 'thinking',
      name: 'Thinking Skills',
      icon: '🧠',
      description: 'Logical reasoning, critical thinking, and problem solving',
      color: '#f59e0b',
      exams: getExamsByTopic('thinking'),
      mockTests: getExamsByTopicAndCategory('thinking', 'MockTests'),
      topicTests: getExamsByTopicAndCategory('thinking', 'TopicBased'),
      paidTests: getExamsByTopicAndCategory('thinking', 'PaidBundle')
    },
    // Writing only for Selective (OC has no writing component)
    ...(userExamType !== 'oc' ? [{
      id: 'writing',
      name: 'Writing',
      icon: '✍️',
      description: 'Creative and persuasive writing skills',
      color: '#ef4444',
      exams: getExamsByTopic('writing'),
      mockTests: getExamsByTopicAndCategory('writing', 'MockTests'),
      topicTests: getExamsByTopicAndCategory('writing', 'TopicBased'),
      paidTests: getExamsByTopicAndCategory('writing', 'PaidBundle')
    }] : [])
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
        <p style={{ fontSize: '18px', color: '#666' }}>Loading practice tests...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '40px', background: '#fee2e2', border: '2px solid #ef4444' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
        <h2 style={{ color: '#dc2626', marginBottom: '15px' }}>Error Loading Tests</h2>
        <p style={{ color: '#991b1b', marginBottom: '20px' }}>{error}</p>
        <button className="btn-primary" onClick={fetchExams}>
          Try Again
        </button>
      </div>
    );
  }

  if (!selectedCategory) {
    return (
      <div>
        <h1 style={{ marginBottom: '30px', textAlign: 'center', color: 'white', fontSize: '36px' }}>
          {userExamType === 'oc' ? 'OC Placement Test Practice' : 'NSW Selective Test Practice'}
        </h1>
        <p style={{ textAlign: 'center', color: 'white', marginBottom: '40px', fontSize: '18px' }}>
          Choose a subject area to begin your practice
        </p>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: '25px',
          marginBottom: '40px',
          maxWidth: '900px',
          margin: '0 auto 40px'
        }}>
          {categories.map(category => (
            <div 
              key={category.id}
              className="card"
              style={{ 
                cursor: 'pointer',
                transition: 'all 0.3s',
                border: `3px solid ${category.color}`,
                position: 'relative',
                overflow: 'hidden'
              }}
              onClick={() => setSelectedCategory(category)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = `0 12px 24px ${category.color}40`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
              }}
            >
              <div style={{ 
                position: 'absolute', 
                top: '-20px', 
                right: '-20px', 
                fontSize: '120px', 
                opacity: '0.1' 
              }}>
                {category.icon}
              </div>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ fontSize: '48px', marginBottom: '15px' }}>{category.icon}</div>
                <h2 style={{ 
                  fontSize: '24px', 
                  marginBottom: '12px', 
                  color: category.color 
                }}>
                  {category.name}
                </h2>
                <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '20px' }}>
                  {category.description}
                </p>
                <div style={{ 
                  display: 'inline-block',
                  padding: '8px 16px',
                  background: category.exams.length > 0 ? category.color : '#9ca3af',
                  color: 'white',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  {category.exams.length > 0 ? `${category.exams.length} Tests Available` : 'Coming Soon'}
                </div>
              </div>
            </div>
          ))}
        </div>

        {exams.length === 0 && (
          <div className="card" style={{ textAlign: 'center', padding: '40px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <h3 style={{ fontSize: '24px', marginBottom: '15px' }}>🚀 Practice Tests Coming Soon!</h3>
            <p style={{ fontSize: '16px', marginBottom: '20px', opacity: '0.9' }}>
              We're preparing comprehensive practice tests for all NSW Selective High School subjects.
              Sign up for early access to premium test bundles!
            </p>
            <button 
              className="btn-primary" 
              style={{ background: 'white', color: '#667eea', fontWeight: '600' }}
              onClick={() => alert('Thank you for your interest! We\'ll notify you when practice tests are available.')}
            >
              Get Notified
            </button>
          </div>
        )}
      </div>
    );
  }

  // Category detail view (when a category is selected)
  return (
    <div>
      <button 
        onClick={() => setSelectedCategory(null)}
        style={{ 
          background: 'white', 
          color: selectedCategory.color,
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
        className="btn-secondary"
      >
        ← Back to Categories
      </button>

      <div className="card" style={{ borderLeft: `6px solid ${selectedCategory.color}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
          <span style={{ fontSize: '48px' }}>{selectedCategory.icon}</span>
          <div>
            <h1 style={{ fontSize: '32px', color: selectedCategory.color, marginBottom: '8px' }}>
              {selectedCategory.name}
            </h1>
            <p style={{ color: '#666', fontSize: '16px' }}>{selectedCategory.description}</p>
          </div>
        </div>
      </div>

      {selectedCategory.exams.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
          {/* Free Practice Tests Section */}
          {selectedCategory.mockTests.length > 0 && (
            <div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                marginBottom: '20px',
                padding: '15px 20px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: '12px',
                color: 'white'
              }}>
                <span style={{ fontSize: '32px' }}>🆓</span>
                <div>
                  <h2 style={{ fontSize: '24px', marginBottom: '4px' }}>Practice Tests (Free)</h2>
                  <p style={{ fontSize: '14px', opacity: '0.9', margin: 0 }}>
                    Full-length mock exams to test your knowledge • {selectedCategory.mockTests.length} available
                  </p>
                </div>
              </div>
              
              <div style={{ display: 'grid', gap: '15px' }}>
                {selectedCategory.mockTests.map(exam => (
                  <Link 
                    key={exam.examId} 
                    to={`/exam/${exam.examId}`}
                    style={{ textDecoration: 'none' }}
                  >
                    <div 
                      className="card"
                      style={{ 
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        borderLeft: `4px solid #10b981`
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateX(8px)';
                        e.currentTarget.style.boxShadow = '0 8px 16px rgba(16, 185, 129, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateX(0)';
                        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                            <h3 style={{ fontSize: '20px', color: '#333', margin: 0 }}>
                              {exam.title}
                            </h3>
                            <span style={{ 
                              fontSize: '11px', 
                              padding: '3px 8px', 
                              background: '#d1fae5', 
                              color: '#065f46',
                              borderRadius: '4px',
                              fontWeight: '600'
                            }}>
                              FREE
                            </span>
                          </div>
                          <p style={{ color: '#666', marginBottom: '15px', lineHeight: '1.6' }}>
                            {exam.description}
                          </p>
                          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#666' }}>
                              <span>📝</span>
                              <span style={{ fontSize: '14px' }}>{exam.questionCount} questions</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#666' }}>
                              <span>⏱️</span>
                              <span style={{ fontSize: '14px' }}>{exam.duration} minutes</span>
                            </div>
                          </div>
                        </div>
                        <div style={{ 
                          padding: '10px 20px',
                          background: '#10b981',
                          color: 'white',
                          borderRadius: '8px',
                          fontWeight: '600',
                          fontSize: '14px',
                          whiteSpace: 'nowrap'
                        }}>
                          Start Test →
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Topic-Based Tests Section - Just show button, no individual tests */}
          {selectedCategory.topicTests.length > 0 && (
            <div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                gap: '12px', 
                padding: '20px 25px',
                background: `linear-gradient(135deg, ${selectedCategory.color} 0%, ${selectedCategory.color}dd 100%)`,
                borderRadius: '12px',
                color: 'white'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <span style={{ fontSize: '36px' }}>🎯</span>
                  <div>
                    <h2 style={{ fontSize: '24px', marginBottom: '4px' }}>Topic-Based Practice</h2>
                    <p style={{ fontSize: '14px', opacity: '0.9', margin: 0 }}>
                      Focused practice on specific skills and topics • {selectedCategory.topicTests.length} available
                    </p>
                  </div>
                </div>
                <Link to="/topic-tests" style={{ textDecoration: 'none' }}>
                  <button style={{
                    padding: '12px 24px',
                    background: 'rgba(255,255,255,0.2)',
                    border: '2px solid white',
                    color: 'white',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.color = selectedCategory.color;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                    e.currentTarget.style.color = 'white';
                  }}>
                    Browse by Topic →
                  </button>
                </Link>
              </div>
            </div>
          )}

          {/* Elite Practice Section - Just show button, no individual tests */}
          {selectedCategory.paidTests.length > 0 && (
            <div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                gap: '12px', 
                padding: '20px 25px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                borderRadius: '12px',
                color: 'white'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <span style={{ fontSize: '36px' }}>💎</span>
                  <div>
                    <h2 style={{ fontSize: '24px', marginBottom: '4px' }}>Elite Practice</h2>
                    <p style={{ fontSize: '14px', opacity: '0.9', margin: 0 }}>
                      Exclusive full-length tests for premium members • {selectedCategory.paidTests.length} available
                    </p>
                  </div>
                </div>
                <Link to="/elite-practice" style={{ textDecoration: 'none' }}>
                  <button style={{
                    padding: '12px 24px',
                    background: 'rgba(255,255,255,0.2)',
                    border: '2px solid white',
                    color: 'white',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.color = '#8b5cf6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                    e.currentTarget.style.color = 'white';
                  }}>
                    Browse Elite Tests →
                  </button>
                </Link>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card" style={{ textAlign: 'center', padding: '60px 24px' }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>📚</div>
          <h2 style={{ fontSize: '28px', marginBottom: '15px', color: '#333' }}>
            Practice Tests Coming Soon
          </h2>
          <p style={{ fontSize: '16px', color: '#666', marginBottom: '30px', maxWidth: '600px', margin: '0 auto 30px' }}>
            We're developing comprehensive {selectedCategory.name.toLowerCase()} practice tests 
            aligned with NSW Selective High School standards. Check back soon or sign up for updates!
          </p>
          <button 
            className="btn-primary" 
            style={{ background: selectedCategory.color }}
            onClick={() => alert('Thank you! We\'ll notify you when ' + selectedCategory.name + ' tests are ready.')}
          >
            Notify Me When Available
          </button>
        </div>
      )}
    </div>
  );
}

export default ExamList;
